import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import logo from "@/assets/logo.png";

const navItems = [
  { label: "Home", path: "/" },
  {
    label: "About Us",
    children: [
      { label: "About New Silkway", path: "/about/nsi" },
      { label: "Our Team", path: "/about/team" },
    ],
  },
  {
    label: "Special Economic Zone",
    children: [
      { label: "Concept", path: "/sez/concept" },
      { label: "Why Central Asia", path: "/sez/why-central-asia" },
      { label: "Investment Opportunities", path: "/sez/investment" },
    ],
  },
  {
    label: "Development Opportunities",
    children: [
      { label: "Inland Dry Port", path: "/development/dry-port" },
      { label: "Manufacturing Park", path: "/development/manufacturing" },
      { label: "Global Financial Center", path: "/development/financial-center" },
      { label: "Duty-Free Retail & Shopping", path: "/development/retail" },
      { label: "Hospitality", path: "/development/hospitality" },
      { label: "Sustainable Energy", path: "/development/energy" },
      { label: "Logistics", path: "/development/logistics" },
      { label: "Medical & Research", path: "/development/medical" },
      { label: "Convention Center & Entertainment", path: "/development/convention" },
      { label: "Customs Facilities", path: "/development/customs" },
    ],
  },
  { label: "Gallery", path: "/gallery" },
  { label: "News", path: "/news" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenDropdown(null), 200);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white ${
        scrolled ? "shadow-md" : "shadow-sm"
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-8 h-24">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img src={logo} alt="NewSilkWay" className="h-16" width={72} height={64} fetchPriority="high" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-0.5">
          {navItems.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                <button className="flex items-center gap-1 px-1.5 py-2 text-xs uppercase tracking-wider text-gray-800 hover:text-primary transition-colors font-body whitespace-nowrap font-medium">
                  {item.label}
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                </button>
                {openDropdown === item.label && (
                  <div className="absolute top-full left-0 mt-1 min-w-[260px] bg-white border border-gray-100 rounded-md shadow-xl py-2 animate-fade-in">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className="block px-5 py-2.5 text-sm text-gray-500 hover:text-primary hover:bg-gray-50 transition-colors"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.path!}
                className="px-1.5 py-2 text-xs uppercase tracking-wider text-gray-800 hover:text-primary transition-colors font-body whitespace-nowrap font-medium"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-gray-700"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button
                    className="flex items-center justify-between w-full py-3 text-sm uppercase tracking-wider text-gray-600"
                    onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                  >
                    {item.label}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${mobileExpanded === item.label ? "rotate-180" : ""}`} />
                  </button>
                  {mobileExpanded === item.label && (
                    <div className="pl-4 space-y-1 pb-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="block py-2 text-sm text-gray-500 hover:text-primary"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.path!}
                  className="block py-3 text-sm uppercase tracking-wider text-gray-600 hover:text-gray-900"
                >
                  {item.label}
                </Link>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
